import { SORTING_COMPARATORS } from "./derivations/comparators";
import { VALUE_DERIVATIONS } from "./derivations/valueDerivations";
import { SEARCH_TERM_RULES } from "./searchTermRules";
import {
  CLAIM_TYPES,
  DERIVATION_TIME_ZONES,
  METRIC_COUNTER_NAME_KINDS,
  SORT_DIRECTIONS,
  TERM_NORMALIZATIONS,
} from "./types";
import type { SearchConfig } from "./types";

export interface ValidationError {
  /** Section or element named in the message, e.g. "table.columns", "searchableFields[1]". */
  readonly path: string;
  readonly message: string;
}

export type ValidationResult =
  | { readonly ok: true; readonly config: SearchConfig }
  | { readonly ok: false; readonly errors: readonly ValidationError[] };

/** Collects one error per defect, so a single result carries them all. */
type ErrorSink = (path: string, message: string) => void;

const ROOT_PATH = "searchConfig";
const COLUMNS_PATH = "table.columns";
const DEFAULT_SORTING_PATH = "table.defaultSorting";

const MIN_COLUMNS = 1;
const MAX_COLUMNS = 50;
const MIN_SEARCHABLE_FIELDS = 1;
const MAX_SEARCHABLE_FIELDS = 10;
const MIN_EXPORT_FIELDS = 1;
const MAX_EXPORT_FIELDS = 50;
const MAX_HEADER_CHARS = 60;
const MAX_EXPORT_LABEL_CHARS = 60;
const MAX_TABLE_LABEL_CHARS = 120;
const MIN_FILE_NAME_PREFIX_CHARS = 1;
const MAX_FILE_NAME_PREFIX_CHARS = 40;
const MIN_TERM_LENGTH = 1;
const MAX_TERM_LENGTH = 100;
const MIN_PAGE_SIZE = 1;
const MAX_PAGE_SIZE = 100;
const MAX_VALIDATION_RULES = 5;

// The closed value sets are imported rather than restated here. A second copy
// would let the validator and the type drift: a value added to the type would
// type-check in a shipped config and then be rejected as unknown at runtime.
// The registry-backed names (comparators, derivations, rule ids) are already
// drift-proof for the same reason, since `isKnownName` reads the live registry.

const METRIC_LIST_KEYS = ["pageVisited", "searchSubmitted"] as const;

const MY_SUBMITTED_CLAIMS_LABEL_KEYS = [
  "tabLabel",
  "resultsHeaderText",
  "limitReachedSuffix",
  "exportFileNamePrefix",
] as const;

const TABLE_OPTIONAL_SUFFIX_KEYS = ["moreResultsSuffix", "narrowDateRangeSuffix"] as const;

const TABLE_LABEL_KEYS = [
  "loadingText",
  "resourceName",
  "resultsHeaderText",
  "filterPlaceholder",
  "filterAriaLabel",
] as const;

const SEARCHABLE_FIELD_LABEL_KEYS = [
  "buttonText",
  "buttonTextSmall",
  "placeholder",
  "placeholderSmall",
] as const;

/**
 * Structurally checks a candidate Search_Config and returns either the accepted
 * config or every defect found, each naming the thing it is about.
 *
 * Total over its input. `null`, `undefined`, a primitive, and an array are all
 * rejected rather than thrown on, and every nested read goes through a type
 * guard, so a document arriving from a backend cannot take the page down. There
 * is deliberately no recursive walk of the candidate: every check addresses a
 * declared path, which keeps the whole pass linear in the declared element
 * count and makes a self-referential input a non-event.
 *
 * Two things this does not check, because checking them would be wrong:
 *
 * - `queryName` against a list of known queries. Named queries live in the
 *   backend's per-claimType manifest and are added without a UI release, so an
 *   allowlist here would reject a query the backend gained yesterday. An
 *   unknown query surfaces as a service error instead.
 * - A Searchable_Field declaring `acceptsDateRange` while the config declares
 *   no `dateRangeRequest`. No shipped config pairs them, but a claim type whose
 *   backend cannot yet accept bounds legitimately would: the dispatcher reports
 *   date-bounded search as unavailable. Rejecting it would replace an honest
 *   message with a page that will not render.
 */
export function validateSearchConfig(candidate: unknown): ValidationResult {
  const errors: ValidationError[] = [];
  const report: ErrorSink = (path, message) => errors.push({ path, message });

  const config = asRecord(candidate);
  if (config === null) {
    report(
      ROOT_PATH,
      `The claims history configuration must be an object; received ${describeValue(candidate)}.`
    );
    return { ok: false, errors };
  }

  const tablePageSize = validateTable(config.table, report);
  validateSearchableFields(config.searchableFields, report);
  validateRetrievalPageSize(config.retrievalPageSize, tablePageSize, report);
  validateExportConfig(config.exportConfig, report);
  validateClaimType(config.claimType, report);
  validateIdentifiers(config, report);
  validateMetrics(config.metrics, report);
  validateDateRangeRequest(config.dateRangeRequest, report);
  validateCountryFilter(config.countryFilter, report);
  validateMySubmittedClaims(config.mySubmittedClaims, report);

  if (errors.length > 0) {
    return { ok: false, errors };
  }
  // Every required attribute, and every optional section that is present, has
  // now been checked against the type, so the cast records what the checks above
  // established rather than asserting past them.
  return { ok: true, config: candidate as SearchConfig };
}

function validateColumns(value: unknown, report: ErrorSink): ReadonlySet<string> {
  const declaredIds = new Set<string>();
  const columns = requireList(value, COLUMNS_PATH, "column definitions", report);
  if (columns === null) {
    return declaredIds;
  }
  if (
    !reportListLength(columns.length, MIN_COLUMNS, MAX_COLUMNS, COLUMNS_PATH, "columns", report) ||
    columns.length === 0
  ) {
    return declaredIds;
  }

  columns.forEach((entry, index) => {
    const path = `${COLUMNS_PATH}[${index}]`;
    const column = asRecord(entry);
    if (column === null) {
      report(path, `Column at index ${index} must be an object; received ${describeValue(entry)}.`);
      return;
    }

    const columnId = column.columnId;
    const describe = isNonEmptyString(columnId)
      ? `Column "${columnId}"`
      : `Column at index ${index}`;

    if (!isNonEmptyString(columnId)) {
      report(`${path}.columnId`, `Column at index ${index} omits its column identifier.`);
    } else if (declaredIds.has(columnId)) {
      report(`${path}.columnId`, `Duplicate column identifier "${columnId}".`);
    } else {
      declaredIds.add(columnId);
    }

    if (!isNonEmptyString(column.header)) {
      report(`${path}.header`, `${describe} omits its header text.`);
    } else if (column.header.length > MAX_HEADER_CHARS) {
      report(
        `${path}.header`,
        `${describe} declares header text of ${column.header.length} characters; at most ${MAX_HEADER_CHARS} are allowed.`
      );
    }

    if (!isNonEmptyString(column.source)) {
      report(`${path}.source`, `${describe} omits the claim property to read.`);
    }

    validateSorting(column.sorting, `${path}.sorting`, describe, report);
    validateDerivation(column.derivation, `${path}.derivation`, describe, report);
    validateOptionalText(
      column.emptyPlaceholder,
      `${path}.emptyPlaceholder`,
      describe,
      "an empty-value placeholder",
      report
    );
    validateOptionalWidth(column.width, `${path}.width`, describe, report);
  });

  return declaredIds;
}

/**
 * An optional display string. The value is returned as a cell value and
 * rendered, so a non-string would reach the table or the CSV as itself.
 */
function validateOptionalText(
  value: unknown,
  path: string,
  describe: string,
  label: string,
  report: ErrorSink
): void {
  if (value !== undefined && typeof value !== "string") {
    report(path, `${describe} declares ${label} ${describeValue(value)} that is not a string.`);
  }
}

/**
 * No upper bound is imposed: an absurd width is a layout problem rather than a
 * defect, and inventing a ceiling would reject a legitimate value. A non-number
 * is rejected because the builder passes this straight to a numeric Cloudscape
 * prop.
 */
function validateOptionalWidth(
  value: unknown,
  path: string,
  describe: string,
  report: ErrorSink
): void {
  if (value === undefined) {
    return;
  }
  if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) {
    report(
      path,
      `${describe} declares column width ${describeValue(value)}; it must be a positive integer.`
    );
  }
}

/**
 * Exactly one sorting selection, so a column cannot sort on a field and a
 * comparator at once and cannot silently sort on nothing.
 */
function validateSorting(value: unknown, path: string, describe: string, report: ErrorSink): void {
  const sorting = asRecord(value);
  if (sorting === null) {
    report(path, `${describe} omits its sorting selection.`);
    return;
  }

  if (sorting.kind === "field") {
    if (hasOwn(sorting, "comparator")) {
      report(
        path,
        `${describe} declares both a sorting field and a sorting comparator; exactly one is allowed.`
      );
    }
    if (!isNonEmptyString(sorting.sortingField)) {
      report(`${path}.sortingField`, `${describe} omits the field name it sorts on.`);
    }
    return;
  }

  if (sorting.kind === "comparator") {
    if (hasOwn(sorting, "sortingField")) {
      report(
        path,
        `${describe} declares both a sorting field and a sorting comparator; exactly one is allowed.`
      );
    }
    if (!isKnownName(sorting.comparator, SORTING_COMPARATORS)) {
      report(
        `${path}.comparator`,
        `${describe} declares unknown sorting comparator ${describeValue(sorting.comparator)}; the implemented comparators are ${namesOf(SORTING_COMPARATORS)}.`
      );
    }
    return;
  }

  report(
    path,
    `${describe} declares sorting selection kind ${describeValue(sorting.kind)}; it must be "field" or "comparator".`
  );
}

function validateDerivation(
  value: unknown,
  path: string,
  describe: string,
  report: ErrorSink
): void {
  if (value === undefined) {
    return;
  }
  const derivation = asRecord(value);
  if (derivation === null) {
    report(path, `${describe} declares a derivation that is not an object.`);
    return;
  }
  if (!isKnownName(derivation.kind, VALUE_DERIVATIONS)) {
    report(
      `${path}.kind`,
      `${describe} declares unknown value derivation ${describeValue(derivation.kind)}; the implemented derivations are ${namesOf(VALUE_DERIVATIONS)}.`
    );
    return;
  }

  // Each derivation reads its own payload unconditionally, so checking the kind
  // alone is not enough: `{ kind: "concatenate" }` would be accepted and then
  // `concatenate` would iterate an absent `parts` and throw, breaking the
  // never-throws guarantee every cell renderer depends on.
  switch (derivation.kind) {
    case "epochSecondsToDate":
      validateEpochSecondsToDate(derivation, path, describe, report);
      return;
    case "optionLabel":
      validateOptionLabel(derivation, path, describe, report);
      return;
    case "concatenate":
      validateConcatenate(derivation, path, describe, report);
      return;
  }
}

function validateEpochSecondsToDate(
  derivation: Readonly<Record<string, unknown>>,
  path: string,
  describe: string,
  report: ErrorSink
): void {
  const timeZone = derivation.timeZone;
  if (timeZone !== undefined && !isOneOf(timeZone, DERIVATION_TIME_ZONES)) {
    report(
      `${path}.timeZone`,
      `${describe} declares time zone ${describeValue(timeZone)}; it must be one of ${DERIVATION_TIME_ZONES.join(", ")}.`
    );
  }
}

function validateOptionLabel(
  derivation: Readonly<Record<string, unknown>>,
  path: string,
  describe: string,
  report: ErrorSink
): void {
  const options = derivation.options;
  if (!Array.isArray(options) || options.length === 0) {
    report(
      `${path}.options`,
      `${describe} declares an "optionLabel" derivation with no options to match against.`
    );
    return;
  }
  options.forEach((entry: unknown, index: number) => {
    const option = asRecord(entry);
    if (option === null) {
      report(
        `${path}.options[${index}]`,
        `${describe} declares an option that is not an object; received ${describeValue(entry)}.`
      );
      return;
    }
    if (typeof option.value !== "string") {
      report(
        `${path}.options[${index}].value`,
        `${describe} declares an option whose value ${describeValue(option.value)} is not a string.`
      );
    }
    if (!isNonEmptyString(option.label)) {
      report(`${path}.options[${index}].label`, `${describe} declares an option with no label.`);
    }
  });
  if (derivation.unmatchedText !== undefined && !isNonEmptyString(derivation.unmatchedText)) {
    report(
      `${path}.unmatchedText`,
      `${describe} declares unmatched text ${describeValue(derivation.unmatchedText)} that is not a non-empty string.`
    );
  }
}

function validateConcatenate(
  derivation: Readonly<Record<string, unknown>>,
  path: string,
  describe: string,
  report: ErrorSink
): void {
  if (typeof derivation.separator !== "string") {
    report(
      `${path}.separator`,
      `${describe} declares a "concatenate" derivation whose separator ${describeValue(derivation.separator)} is not a string.`
    );
  }
  const parts = derivation.parts;
  if (!Array.isArray(parts) || parts.length === 0) {
    report(
      `${path}.parts`,
      `${describe} declares a "concatenate" derivation with no parts to join.`
    );
    return;
  }
  parts.forEach((entry: unknown, index: number) => {
    const part = asRecord(entry);
    if (part === null) {
      report(
        `${path}.parts[${index}]`,
        `${describe} declares a part that is not an object; received ${describeValue(entry)}.`
      );
      return;
    }
    if (!isNonEmptyString(part.source)) {
      report(
        `${path}.parts[${index}].source`,
        `${describe} declares a part omitting the claim property to read.`
      );
    }
    if (typeof part.required !== "boolean") {
      report(
        `${path}.parts[${index}].required`,
        `${describe} declares a part whose "required" flag ${describeValue(part.required)} is not a boolean.`
      );
    }
  });
}

function validateSearchableFields(value: unknown, report: ErrorSink): void {
  const fields = requireList(value, "searchableFields", "searchable fields", report);
  if (fields === null) {
    return;
  }
  if (
    !reportListLength(
      fields.length,
      MIN_SEARCHABLE_FIELDS,
      MAX_SEARCHABLE_FIELDS,
      "searchableFields",
      "searchable fields",
      report
    ) ||
    fields.length === 0
  ) {
    return;
  }

  const declaredIds = new Set<string>();
  fields.forEach((entry, index) => {
    const path = `searchableFields[${index}]`;
    const field = asRecord(entry);
    if (field === null) {
      report(
        path,
        `Searchable field at index ${index} must be an object; received ${describeValue(entry)}.`
      );
      return;
    }

    const fieldId = field.fieldId;
    const describe = isNonEmptyString(fieldId)
      ? `Searchable field "${fieldId}"`
      : `Searchable field at index ${index}`;

    if (!isNonEmptyString(fieldId)) {
      report(`${path}.fieldId`, `Searchable field at index ${index} omits its field identifier.`);
    } else if (declaredIds.has(fieldId)) {
      report(`${path}.fieldId`, `Duplicate searchable field identifier "${fieldId}".`);
    } else {
      declaredIds.add(fieldId);
    }

    validateSearchableFieldLabels(field.labels, path, describe, report);
    validateMaxTermLength(field.maxTermLength, path, describe, report);
    validateRetrievalAttributes(field, path, describe, report);

    if (field.normalization !== undefined && !isOneOf(field.normalization, TERM_NORMALIZATIONS)) {
      report(
        `${path}.normalization`,
        `${describe} declares search term normalization ${describeValue(field.normalization)}; it must be one of ${TERM_NORMALIZATIONS.join(", ")}.`
      );
    }

    // Checked as a boolean rather than read for truthiness: the string "false"
    // is truthy, and would switch on the date-bounded path it names.
    if (field.acceptsDateRange !== undefined && typeof field.acceptsDateRange !== "boolean") {
      report(
        `${path}.acceptsDateRange`,
        `${describe} declares "acceptsDateRange" ${describeValue(field.acceptsDateRange)}; it must be a boolean.`
      );
    }

    validateValidationRules(field.validationRules, path, describe, report);

    // Guarded because this list is optional, unlike the top-level `metrics`
    // lists and `mySubmittedClaims.metrics`, and `validateMetricDeclarations`
    // reports on any non-array including `undefined`.
    if (field.metrics !== undefined) {
      validateMetricDeclarations(field.metrics, `${path}.metrics`, report);
    }
  });
}

function validateSearchableFieldLabels(
  value: unknown,
  path: string,
  describe: string,
  report: ErrorSink
): void {
  const labels = asRecord(value);
  if (labels === null) {
    report(`${path}.labels`, `${describe} omits its labels.`);
    return;
  }
  for (const key of SEARCHABLE_FIELD_LABEL_KEYS) {
    if (!isNonEmptyString(labels[key])) {
      report(`${path}.labels.${key}`, `${describe} omits its "${key}" label.`);
    }
  }
}

function validateMaxTermLength(
  value: unknown,
  path: string,
  describe: string,
  report: ErrorSink
): void {
  if (value === undefined || value === null) {
    report(`${path}.maxTermLength`, `${describe} omits its maximum search term length.`);
    return;
  }
  if (!isIntegerInRange(value, MIN_TERM_LENGTH, MAX_TERM_LENGTH)) {
    report(
      `${path}.maxTermLength`,
      `${describe} declares a maximum search term length of ${describeValue(value)}; it must be an integer from ${MIN_TERM_LENGTH} to ${MAX_TERM_LENGTH}.`
    );
  }
}

/**
 * The two attributes that turn a selected field into a `POST /listClaims`
 * request. `queryName` is checked for presence only: the set of named queries
 * lives in a backend manifest that grows without a UI release.
 */
function validateRetrievalAttributes(
  field: Readonly<Record<string, unknown>>,
  path: string,
  describe: string,
  report: ErrorSink
): void {
  if (!isNonEmptyString(field.queryName)) {
    report(`${path}.queryName`, `${describe} omits its "queryName".`);
  }
  if (!isNonEmptyString(field.queryInputProperty)) {
    report(`${path}.queryInputProperty`, `${describe} omits its "queryInputProperty".`);
  }
}

function validateValidationRules(
  value: unknown,
  path: string,
  describe: string,
  report: ErrorSink
): void {
  if (value === undefined) {
    return;
  }
  if (!Array.isArray(value)) {
    report(`${path}.validationRules`, `${describe} declares validation rules that are not a list.`);
    return;
  }
  if (value.length > MAX_VALIDATION_RULES) {
    report(
      `${path}.validationRules`,
      `${describe} declares ${value.length} validation rules; at most ${MAX_VALIDATION_RULES} are allowed.`
    );
  }

  value.forEach((entry, index) => {
    const rulePath = `${path}.validationRules[${index}]`;
    const rule = asRecord(entry);
    if (rule === null) {
      report(rulePath, `${describe} declares a validation rule that is not an object.`);
      return;
    }
    if (!isKnownName(rule.ruleId, SEARCH_TERM_RULES)) {
      report(
        `${rulePath}.ruleId`,
        `${describe} declares unknown search term validation rule ${describeValue(rule.ruleId)}; the implemented rules are ${namesOf(SEARCH_TERM_RULES)}.`
      );
    }
    if (!isNonEmptyString(rule.message)) {
      report(
        `${rulePath}.message`,
        `${describe} omits the message for one of its validation rules.`
      );
    }
  });
}

function validateDefaultSorting(
  value: unknown,
  declaredColumnIds: ReadonlySet<string>,
  report: ErrorSink
): void {
  const sorting = asRecord(value);
  if (sorting === null) {
    report(DEFAULT_SORTING_PATH, `Required section "${DEFAULT_SORTING_PATH}" is missing.`);
    return;
  }

  const columnId = sorting.columnId;
  if (!isNonEmptyString(columnId)) {
    report(
      `${DEFAULT_SORTING_PATH}.columnId`,
      `Section "${DEFAULT_SORTING_PATH}" omits its column identifier.`
    );
  } else if (declaredColumnIds.size > 0 && !declaredColumnIds.has(columnId)) {
    // Only meaningful once the column list itself parsed; otherwise the empty
    // set would report every id as unmatched on top of the column error.
    report(
      `${DEFAULT_SORTING_PATH}.columnId`,
      `Default sorting column identifier "${columnId}" matches no declared columnId.`
    );
  }

  if (!isOneOf(sorting.direction, SORT_DIRECTIONS)) {
    report(
      `${DEFAULT_SORTING_PATH}.direction`,
      `Default sort direction ${describeValue(sorting.direction)} is neither "ascending" nor "descending".`
    );
  }
}

/**
 * Returns the accepted page size, or null when it failed its own check.
 *
 * A missing `table` reports once and stops rather than also reporting its
 * columns and default sorting as missing, the same way a missing `exportConfig`
 * does not additionally report its fields.
 */
function validateTable(value: unknown, report: ErrorSink): number | null {
  const table = asRecord(value);
  if (table === null) {
    report("table", `Required section "table" is missing.`);
    return null;
  }

  const columnIds = validateColumns(table.columns, report);
  validateDefaultSorting(table.defaultSorting, columnIds, report);

  const pageSize = table.pageSize;
  const pageSizeValid = isIntegerInRange(pageSize, MIN_PAGE_SIZE, MAX_PAGE_SIZE);
  if (pageSize === undefined || pageSize === null) {
    report("table.pageSize", `Section "table" omits its page size.`);
  } else if (!pageSizeValid) {
    report(
      "table.pageSize",
      `Table page size ${describeValue(pageSize)} must be an integer from ${MIN_PAGE_SIZE} to ${MAX_PAGE_SIZE}.`
    );
  }

  validateMinItemsForControls(table.minItemsForControls, pageSizeValid ? pageSize : null, report);

  for (const key of TABLE_LABEL_KEYS) {
    const label = table[key];
    if (!isNonEmptyString(label)) {
      report(`table.${key}`, `Section "table" omits its "${key}" text.`);
    } else if (label.length > MAX_TABLE_LABEL_CHARS) {
      report(
        `table.${key}`,
        `Table "${key}" text is ${label.length} characters; at most ${MAX_TABLE_LABEL_CHARS} are allowed.`
      );
    }
  }

  // Appended to the results header, so a non-string would be concatenated into
  // it as itself. Optional, unlike the labels above, because not every line of
  // business declares them.
  for (const key of TABLE_OPTIONAL_SUFFIX_KEYS) {
    validateOptionalText(
      table[key],
      `table.${key}`,
      `Section "table"`,
      `its "${key}" text`,
      report
    );
  }

  return pageSizeValid ? pageSize : null;
}

/**
 * The retrieved set must be at least a rendered page, or the table pages over
 * rows the search never fetched. `tablePageSize` is null when it failed its own
 * check, so one defect is reported once.
 */
function validateRetrievalPageSize(
  value: unknown,
  tablePageSize: number | null,
  report: ErrorSink
): void {
  const path = "retrievalPageSize";
  if (value === undefined || value === null) {
    report(path, `Required attribute "retrievalPageSize" is missing.`);
    return;
  }
  if (!isIntegerInRange(value, MIN_PAGE_SIZE, MAX_PAGE_SIZE)) {
    report(
      path,
      `Retrieval page size ${describeValue(value)} must be an integer from ${MIN_PAGE_SIZE} to ${MAX_PAGE_SIZE}.`
    );
    return;
  }
  if (tablePageSize !== null && value < tablePageSize) {
    report(
      path,
      `Retrieval page size (${value}) is below the table page size (${tablePageSize}), so a rendered page could hold more rows than the search retrieves.`
    );
  }
}

/** `pageSize` is null when it failed its own check, so one defect is reported once. */
function validateMinItemsForControls(
  value: unknown,
  pageSize: number | null,
  report: ErrorSink
): void {
  const path = "table.minItemsForControls";
  if (value === undefined || value === null) {
    report(path, `Section "table" omits its minimum item count for controls.`);
    return;
  }
  if (!isIntegerInRange(value, 0, MAX_PAGE_SIZE)) {
    report(
      path,
      `Table minimum item count for controls ${describeValue(value)} must be an integer from 0 to the page size.`
    );
    return;
  }
  if (pageSize !== null && value > pageSize) {
    report(
      path,
      `Table minimum item count for controls (${value}) exceeds the page size (${pageSize}).`
    );
  }
}

function validateExportConfig(value: unknown, report: ErrorSink): void {
  const exportConfig = asRecord(value);
  if (exportConfig === null) {
    report("exportConfig", `Required section "exportConfig" is missing.`);
    return;
  }

  validateExportFields(exportConfig.fields, report);
  validateFileNamePrefix(exportConfig.fileNamePrefix, report);
}

function validateExportFields(value: unknown, report: ErrorSink): void {
  const fields = requireList(value, "exportConfig.fields", "export fields", report);
  if (fields === null) {
    return;
  }
  if (
    !reportListLength(
      fields.length,
      MIN_EXPORT_FIELDS,
      MAX_EXPORT_FIELDS,
      "exportConfig.fields",
      "export fields",
      report
    ) ||
    fields.length === 0
  ) {
    return;
  }

  fields.forEach((entry, index) => {
    const path = `exportConfig.fields[${index}]`;
    const field = asRecord(entry);
    if (field === null) {
      report(
        path,
        `Export field at index ${index} must be an object; received ${describeValue(entry)}.`
      );
      return;
    }

    const label = field.label;
    const describe = isNonEmptyString(label)
      ? `Export field "${label}"`
      : `Export field at index ${index}`;

    if (!isNonEmptyString(label)) {
      report(`${path}.label`, `Export field at index ${index} omits its label.`);
    } else if (label.length > MAX_EXPORT_LABEL_CHARS) {
      report(
        `${path}.label`,
        `${describe} declares a label of ${label.length} characters; at most ${MAX_EXPORT_LABEL_CHARS} are allowed.`
      );
    }

    if (!isNonEmptyString(field.source)) {
      report(`${path}.source`, `${describe} omits the claim property to read.`);
    }

    validateDerivation(field.derivation, `${path}.derivation`, describe, report);
    validateOptionalText(
      field.emptyPlaceholder,
      `${path}.emptyPlaceholder`,
      describe,
      "an empty-value placeholder",
      report
    );
  });
}

function validateFileNamePrefix(value: unknown, report: ErrorSink): void {
  const path = "exportConfig.fileNamePrefix";
  if (!isNonEmptyString(value)) {
    report(path, `Section "exportConfig" omits its export file name prefix.`);
    return;
  }
  if (value.length < MIN_FILE_NAME_PREFIX_CHARS || value.length > MAX_FILE_NAME_PREFIX_CHARS) {
    report(
      path,
      `Export file name prefix "${value}" is ${value.length} characters; it must be from ${MIN_FILE_NAME_PREFIX_CHARS} to ${MAX_FILE_NAME_PREFIX_CHARS}.`
    );
  }
  if (/\s/.test(value)) {
    report(path, `Export file name prefix "${value}" contains whitespace.`);
  }
}

function validateClaimType(value: unknown, report: ErrorSink): void {
  if (value === undefined || value === null || value === "") {
    report("claimType", `Required attribute "claimType" is missing.`);
    return;
  }
  if (!isOneOf(value, CLAIM_TYPES)) {
    report(
      "claimType",
      `Claim type ${describeValue(value)} is not one of ${CLAIM_TYPES.join(", ")}.`
    );
  }
}

/**
 * `lob` is checked for presence only, not against the known LobKey set: the
 * registry is what pairs a config with a key, and a mismatch between the two is
 * asserted there rather than here.
 */
function validateIdentifiers(config: Readonly<Record<string, unknown>>, report: ErrorSink): void {
  if (!isNonEmptyString(config.lob)) {
    report("lob", `Required attribute "lob" is missing.`);
  }
  if (!isNonEmptyString(config.testId)) {
    report("testId", `Required attribute "testId" is missing.`);
  }
}

function validateMetrics(value: unknown, report: ErrorSink): void {
  const metrics = asRecord(value);
  if (metrics === null) {
    report("metrics", `Required section "metrics" is missing.`);
    return;
  }
  if (!isNonEmptyString(metrics.configRetrievalPublisherName)) {
    report(
      "metrics.configRetrievalPublisherName",
      `Section "metrics" omits its config retrieval publisher name.`
    );
  }
  for (const key of METRIC_LIST_KEYS) {
    validateMetricDeclarations(metrics[key], `metrics.${key}`, report);
  }
}

/**
 * An empty list is accepted: a line of business publishing nothing on a given
 * event is a legitimate configuration, unlike an absent list, which means the
 * section was never declared.
 */
function validateMetricDeclarations(value: unknown, path: string, report: ErrorSink): void {
  if (!Array.isArray(value)) {
    report(
      path,
      `Section "${path}" must be a list of metric declarations; received ${describeValue(value)}.`
    );
    return;
  }
  value.forEach((entry: unknown, index: number) => {
    const elementPath = `${path}[${index}]`;
    const declaration = asRecord(entry);
    if (declaration === null) {
      report(
        elementPath,
        `Metric declaration at index ${index} must be an object; received ${describeValue(entry)}.`
      );
      return;
    }
    if (!isNonEmptyString(declaration.publisherName)) {
      report(
        `${elementPath}.publisherName`,
        `Metric declaration at index ${index} omits its publisher name.`
      );
    }
    validateMetricCounterName(declaration.counterName, `${elementPath}.counterName`, index, report);
  });
}

function validateMetricCounterName(
  value: unknown,
  path: string,
  index: number,
  report: ErrorSink
): void {
  const counterName = asRecord(value);
  if (counterName === null) {
    report(path, `Metric declaration at index ${index} omits its counter name.`);
    return;
  }
  if (!isOneOf(counterName.kind, METRIC_COUNTER_NAME_KINDS)) {
    report(
      `${path}.kind`,
      `Metric declaration at index ${index} declares counter name kind ${describeValue(counterName.kind)}; it must be one of ${METRIC_COUNTER_NAME_KINDS.join(", ")}.`
    );
    return;
  }
  // Only the literal form carries a value; the runtime forms resolve theirs when
  // the metric is published.
  if (counterName.kind === "literal" && !isNonEmptyString(counterName.value)) {
    report(
      `${path}.value`,
      `Metric declaration at index ${index} declares a literal counter name with no value.`
    );
  }
}

/**
 * Absent means no named query accepts date range bounds, which is a legitimate
 * state. Present but incomplete is not: the dispatcher would write the bounds
 * under the key "undefined" and the service would answer an unbounded query.
 */
function validateDateRangeRequest(value: unknown, report: ErrorSink): void {
  if (value === undefined) {
    return;
  }
  const request = asRecord(value);
  if (request === null) {
    report(
      "dateRangeRequest",
      `Section "dateRangeRequest" must be an object; received ${describeValue(value)}.`
    );
    return;
  }
  if (!isNonEmptyString(request.startQueryInputProperty)) {
    report(
      "dateRangeRequest.startQueryInputProperty",
      `Section "dateRangeRequest" omits the request property carrying the range start.`
    );
  }
  if (!isNonEmptyString(request.endQueryInputProperty)) {
    report(
      "dateRangeRequest.endQueryInputProperty",
      `Section "dateRangeRequest" omits the request property carrying the range end.`
    );
  }
}

function validateCountryFilter(value: unknown, report: ErrorSink): void {
  if (value === undefined) {
    return;
  }
  const filter = asRecord(value);
  if (filter === null) {
    report(
      "countryFilter",
      `Section "countryFilter" must be an object; received ${describeValue(value)}.`
    );
    return;
  }
  if (!isNonEmptyString(filter.queryInputProperty)) {
    report(
      "countryFilter.queryInputProperty",
      `Section "countryFilter" omits the request property carrying the selected countries.`
    );
  }
}

function validateMySubmittedClaims(value: unknown, report: ErrorSink): void {
  if (value === undefined) {
    return;
  }
  const mode = asRecord(value);
  if (mode === null) {
    report(
      "mySubmittedClaims",
      `Section "mySubmittedClaims" must be an object; received ${describeValue(value)}.`
    );
    return;
  }
  if (!isNonEmptyString(mode.queryName)) {
    report("mySubmittedClaims.queryName", `Section "mySubmittedClaims" omits its "queryName".`);
  }
  if (!isNonEmptyString(mode.queryInputProperty)) {
    report(
      "mySubmittedClaims.queryInputProperty",
      `Section "mySubmittedClaims" omits its "queryInputProperty".`
    );
  }
  if (!isIntegerInRange(mode.pageSize, MIN_PAGE_SIZE, MAX_PAGE_SIZE)) {
    report(
      "mySubmittedClaims.pageSize",
      `Section "mySubmittedClaims" declares page size ${describeValue(mode.pageSize)}; it must be an integer from ${MIN_PAGE_SIZE} to ${MAX_PAGE_SIZE}.`
    );
  }
  for (const key of MY_SUBMITTED_CLAIMS_LABEL_KEYS) {
    if (!isNonEmptyString(mode[key])) {
      report(`mySubmittedClaims.${key}`, `Section "mySubmittedClaims" omits its "${key}" text.`);
    }
  }
  validateMetricDeclarations(mode.metrics, "mySubmittedClaims.metrics", report);
}

/**
 * Reports an absent or non-list section and returns null, so a caller iterates
 * only over something it can iterate over.
 */
function requireList(
  value: unknown,
  path: string,
  description: string,
  report: ErrorSink
): readonly unknown[] | null {
  if (value === undefined || value === null) {
    report(path, `Required section "${path}" is missing.`);
    return null;
  }
  if (!Array.isArray(value)) {
    report(
      path,
      `Section "${path}" must be a list of ${description}; received ${describeValue(value)}.`
    );
    return null;
  }
  return value;
}

/** Returns false when the length is outside the declared bounds. */
function reportListLength(
  length: number,
  min: number,
  max: number,
  path: string,
  description: string,
  report: ErrorSink
): boolean {
  if (length === 0) {
    report(path, `Section "${path}" declares zero ${description}; ${min} to ${max} are required.`);
    return false;
  }
  if (length > max) {
    report(
      path,
      `Section "${path}" declares ${length} ${description}; ${min} to ${max} are required.`
    );
    return false;
  }
  return true;
}

function asRecord(value: unknown): Readonly<Record<string, unknown>> | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  return value as Readonly<Record<string, unknown>>;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isIntegerInRange(value: unknown, min: number, max: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

function isOneOf(value: unknown, allowed: readonly string[]): boolean {
  return typeof value === "string" && allowed.includes(value);
}

function hasOwn(record: Readonly<Record<string, unknown>>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

/**
 * Own keys only, so a candidate naming an inherited property such as
 * `constructor` is not mistaken for a registered name.
 */
function isKnownName(value: unknown, registry: Readonly<Record<string, unknown>>): value is string {
  return typeof value === "string" && hasOwn(registry, value);
}

function namesOf(registry: Readonly<Record<string, unknown>>): string {
  return Object.keys(registry).join(", ");
}

/**
 * A value as it reads inside a message. Never interpolates the value directly:
 * a symbol throws when coerced by a template literal, and an object would read
 * as "[object Object]" without saying what it was.
 */
function describeValue(value: unknown): string {
  if (typeof value === "string") {
    return `"${value}"`;
  }
  if (value === null) {
    return "null";
  }
  if (value === undefined) {
    return "undefined";
  }
  if (Array.isArray(value)) {
    return "an array";
  }
  if (typeof value === "object" || typeof value === "function" || typeof value === "symbol") {
    return `a value of type ${typeof value}`;
  }
  return String(value);
}
